<template>
    <b-modal
        id="themesAvailable"
        :title-html="title"
        :no-close-on-backdrop="true"
        @close="finishedModal"
        size="xl"
        hide-footer>

        <b-alert
            :show="countThemes === 0"
            variant="info"
            :dismissible="false"
            class="mb-3">
            Você não tem temas específicos para essa turma, então consideramos que <b>os alunos podem escolher qualquer tema disponível.</b> Caso queira, você pode incluir temas específicos para essa turma.
        </b-alert>

        <!-- Filters -->
        <div class="row mb-2">
            <div class="col-sm-6">
                <!-- Field -->
                <b-form-group label="Título">
                    <b-form-input
                        v-model="filter.title"
                        placeholder="Buscar por título ..."
                        debounce="500"
                    ></b-form-input>
                </b-form-group>
            </div>
            <div class="col-sm-6">
                <!-- Field -->
                <b-form-group label="Modo de Correção">
                    <multiselect
                        v-model="filter.mode_id"
                        :options="modes"
                        :loading="loading"
                        track-by="id"
                        label="name"
                        placeholder="Buscar por Modo de Correção ..."
                        tag-placeholder="Filtrar Modo de Correção"
                        selectLabel="Selecionar"
                        deselectLabel="Desmarcar"
                        selectedLabel="Selecionado">
                            <span slot="noResult">
                                Ops! Nenhum resultado encontrado na pesquisa :(
                            </span>

                            <span slot="noOptions">
                                Não há modos de correção disponíveis para exibir :(
                            </span>
                    </multiselect>
                </b-form-group>
            </div>
            <div class="col-sm-6">
                <!-- Field -->
                <b-form-group
                    label="Tags">

                    <!-- Input -->
                    <multiselect
                        v-model="filter.tags"
                        :options="tags"
                        :multiple="true"
                        placeholder="Filtrar por Tag"
                        selectLabel="Filtrar por Tag"
                        deselectLabel="Desmarcar"
                        selectedLabel="Selecionado">

                            <span slot="noResult">
                                Ops! Nenhum resultado encontrado na pesquisa :(
                            </span>

                            <span slot="noOptions">
                                Não há tags marcadas no momento.
                            </span>
                    </multiselect>
                </b-form-group>
            </div>
            <div class="col-sm-6">
                <!-- Field -->
                <b-form-group label="Status">
                    <multiselect
                        v-model="filter.status"
                        :options="optionsStatus"
                        :loading="loading"
                        track-by="id"
                        label="name"
                        placeholder="Filtra por Status ..."
                        tag-placeholder="Filtra por Status ..."
                        selectLabel="Selecionar"
                        deselectLabel="Desmarcar"
                        selectedLabel="Selecionado">
                            <span slot="noResult">
                                Ops! Nenhum resultado encontrado na pesquisa :(
                            </span>

                            <span slot="noOptions">
                                Não há status disponíveis para exibir :(
                            </span>
                    </multiselect>
                </b-form-group>
            </div>
        </div>

        <div class="mb-3">
            <b-skeleton-wrapper :loading="loading">

                <!-- Loading -->
                <template #loading>
                    <b-skeleton-table
                        :rows="lastResultsCount"
                        :columns="5"
                        :table-props="{ bordered: true, striped: false }"
                    >
                    </b-skeleton-table>
                </template>

                <!-- Table -->
                <b-table
                    id="themeTable"
                    :items="table"
                    :fields.sync="fields"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    @sort-changed="changeSortable"
                    empty-text="Não encontramos nenhum resultado na busca :("
                    class="bg-white mb-0"
                    stacked="md"
                    :sticky-header="true"
                    show-empty
                    no-local-sorting
                    bordered
                    hover>

                    <template v-slot:cell(title)="row">
                        <a :href="`/teacher/themes/${row.item.id}`" target="_blank" v-b-tooltip.hover title="Ver Detalhes">
                            {{ row.item.title }}
                        </a>
                    </template>

                    <template v-slot:cell(created_at)="row">
                        {{ row.item.created_at | moment('from', 'now') }}
                    </template>

                    <template v-slot:cell(tags)="row">
                        <div v-if="row.item.tags.length === 0">
                            ---
                        </div>
                        <div v-else>
                            <span class="badge badge-primary mr-1" v-for="(tag, tagIdx) in row.item.tags" :key="tagIdx">
                                {{ tag.name }}
                            </span>
                        </div>
                    </template>

                    <template v-slot:cell(status)="row">
                        <div v-if="row.item.groups.length === 0" class="text-success">
                            Incluso
                            <i class="fas fa-info-circle" v-b-tooltip.hover title="Esse tema está disponível para todas as turmas, pois não há turmas específicas para ele."></i>
                        </div>
                        <div v-else-if="existAlreadyGroup(row.item.groups)" class="text-success">
                            Incluso
                        </div>
                        <div v-else class="text-muted">
                            Não Incluso
                        </div>
                    </template>

                    <template v-slot:cell(actions)="row">
                        <div class="text-center">
                            <div v-if="row.item.groups.length === 0">
                                <a href="javascript:void(1);" @click="attachTheme(row.item)" v-b-tooltip.hover title="Adicionar Tema Exclusivamente a essa Turma" class="text-success">
                                    <i class="fas fa-plus-circle"></i>
                                </a>
                            </div>
                            <div v-else-if="existAlreadyGroup(row.item.groups)">
                                <a href="javascript:void(1);" @click="detachTheme(row.item)" v-b-tooltip.hover title="Remover Tema" class="text-danger">
                                    <i class="fas fa-minus-circle"></i>
                                </a>
                            </div>
                            <div v-else class="text-muted">
                                <a href="javascript:void(1);" @click="attachTheme(row.item)" v-b-tooltip.hover title="Adicionar Tema" class="text-success">
                                    <i class="fas fa-plus-circle"></i>
                                </a>
                            </div>
                        </div>
                    </template>
                </b-table>
            </b-skeleton-wrapper>
        </div>

        <!-- Paginate -->
        <b-pagination
            v-model="currentPage"
            :total-rows="total"
            :per-page="perPage"
            @change="changePagination"
        />

        <!-- Results -->
        <div class="mb-4">
            {{ total }} resultados encontrados.
        </div>

    </b-modal>
</template>

<script>
export default {
    props: [],
    data() {
        return {
            title: '',
            countThemes: 0,
            groupId: null,
            loading: false,
            table: [],
            perPage: 6,
            currentPage: 1,
            total: 0,
            sortBy: 'created_at',
            sortDesc: true,
            fields: [
                { key: 'title', label: 'Título', sortable: true },
                { key: 'mode.name', label: 'Modo de Correção', sortable: false },
                { key: 'tags', label: 'Tags', sortable: false },
                { key: 'created_at', label: 'Criado', sortable: true },
                { key: 'status', label: 'Status' },
                { key: 'actions', label: '' }
            ],
            filter: {
                title: null,
                mode_id: null,
                group_id: null,
                tags: [],
                status: null,
            },
            lastResultsCount: 5,
            modes: [],
            tags: [],
            optionsStatus: [
                {
                    id: 'all',
                    name: 'Todos os temas inclusos e não inclusos'
                },
                {
                    id: 'included',
                    name: 'Apenas os temas inclusos'
                },
                {
                    id: 'not_included',
                    name: 'Apenas os temas não inclusos'
                }
            ],
        }
    },
    watch: {
        'filter.title': async function (value) {
            this.filter.title = await value;
            this.currentPage = 1
            await this.load()
        },
        'filter.mode_id': async function (value) {
            this.filter.mode_id = await value;
            this.currentPage = 1
            await this.load()
        },
        'filter.tags': async function (value) {
            this.filter.tags = await value;
            this.currentPage = 1
            await this.load()
        },
        'filter.status': async function (value) {
            this.filter.status = await value;
            this.currentPage = 1
            await this.load()
        },
    },
    created() {
        this.$root.$on('showThemes', async (group) => {
            // Set Form
            this.group = group;
            this.groupId = group.id;

            this.filter.status = this.optionsStatus[0];

            this.setTitleModal(group);

            this.load();

            // Modal
            this.$bvModal.show('themesAvailable')
        })
    },
    methods: {

        load() {
            this.loading = true;
            axios.get('/teacher/groups/' + this.groupId + '/themes', {
                params: {
                    title: this.filter.title,
                    mode_id: this.filter.mode_id ? this.filter.mode_id.id : null,
                    tags: this.filter.tags,
                    status: this.filter.status ? this.filter.status.id : 'all',

                    page: this.currentPage,
                    limit: this.perPage,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc ? 'desc': 'asc'
                }
            })
                .then((result) => {
                    const response = result.data.data;

                    this.table = response.themes.data
                    this.currentPage = response.themes.current_page
                    this.perPage = response.themes.per_page
                    this.total = response.themes.total

                    this.lastResultsCount = response.themes.data.length

                    this.modes = response.modes
                    this.tags = response.tags.map(tag => tag.name)
                    this.countThemes = response.count

                    this.setTitleModal(this.group);
                })
                .finally(() => this.loading = false)
        },

        async changePagination(page) {
            this.currentPage = await page;
            this.load()
        },

        async changeSortable(context) {
            this.sortBy = await context.sortBy;
            this.sortDesc = await context.sortDesc
            this.load()
        },

        existAlreadyGroup(groups) {

            if (groups.length === 0) {
                return true;
            }

            return groups.filter(group => group.id === this.groupId).length > 0
        },

        detachTheme(theme) {
            this.loading = true;
            axios.delete('/teacher/groups/' + this.groupId + '/themes/' + theme.id + '/detach')
                .then((result) => {
                    this.load()
                })
                .finally(() => this.loading = false)
        },

        attachTheme(theme) {
            this.loading = true;
            axios.post('/teacher/groups/' + this.groupId + '/themes/' + theme.id + '/attach')
                .then((result) => {
                    this.load()
                })
                .finally(() => this.loading = false)
        },

        setTitleModal(group){
            let name = 'Todos os Temas Disponíveis';

            if (this.countThemes > 0) {
                if (this.countThemes === 1) {
                    name = this.countThemes + ' Tema Disponível';
                } else {
                    name = this.countThemes + ' Temas Disponíveis';
                }
            }

            this.title = 'Turma: ' + group.name + ' - <b>' + name + '<b/>';
        },

        finishedModal() {
            this.$bvModal.hide('themesAvailable')
            this.$emit('updated')
        }
    }
}
</script>

<style scoped>

</style>
